import { useState, useEffect } from 'react';

// This resolves `ReferenceError: window is not defined` when using in NextJS.
const isBrowser = () => typeof window !== 'undefined';

export const BREAKPOINTS = {
  SMALL_MAX_WIDTH: 480,
  MEDIUM_MAX_WIDTH: 640,
  LARGE_MAX_WIDTH: 960,
  XL_MAX_WIDTH: 1440,
};

export const LABELS = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
  XL: 'xl',
};

const SMALL = 0;
const MEDIUM = 1;
const LARGE = 2;
const XL = 3;

const smallMql =
  isBrowser() &&
  window.matchMedia(`(max-width: ${BREAKPOINTS.SMALL_MAX_WIDTH}px)`);

const mediumMql =
  isBrowser() &&
  window.matchMedia(
    `(min-width: ${BREAKPOINTS.SMALL_MAX_WIDTH + 1}px) and (max-width: ${
      BREAKPOINTS.MEDIUM_MAX_WIDTH
    }px)`,
  );
const largeMql =
  isBrowser() &&
  window.matchMedia(
    `(min-width: ${BREAKPOINTS.MEDIUM_MAX_WIDTH + 1}px) and (max-width: ${
      BREAKPOINTS.LARGE_MAX_WIDTH
    }px)`,
  );

const xlMql =
  isBrowser() &&
  window.matchMedia(`(min-width: ${BREAKPOINTS.LARGE_MAX_WIDTH + 1}px)`);

/**
 * A function which gets the current breakpoint variant.
 * Used only in this module as the lazy initial state function.
 */
function getInitialVariant() {
  // Logical AND(&&) resolves `matches' does not exist on type 'false | MediaQueryList` Typescript error
  if (smallMql && smallMql.matches) {
    return SMALL;
  }
  if (mediumMql && mediumMql.matches) {
    return MEDIUM;
  }
  if (largeMql && largeMql.matches) {
    return LARGE;
  }
  return XL;
}

/**
 * @typedef {Object} Breakpoints
 * @property {boolean} isSmall - Whether the small breakpoint is reached
 * @property {boolean} isMedium - Whether the medium breakpoint is reached
 * @property {boolean} isLarge - Whether the large breakpoint is reached
 * @property {boolean} isXL - Whether the large breakpoint is reached
 */

/**
 * A hook which returns an enum value indicating the current matched
 * breakpoint. This module exports the enum values for the three variants
 * (SMALL, MEDIUM, LARGE, and X_LARGE), so consumers of this hook can reference the
 * matched value accordingly.
 *
 *
 * @return {Breakpoints} An object of boolean properties, e.g. `isSmall`
 */

export default function useBreakpoints() {
  const [variant, setVariant] = useState(getInitialVariant);

  useEffect(() => {
    function handleSmall(e: MediaQueryListEvent) {
      if (e.matches) {
        setVariant(SMALL);
      }
    }

    function handleMedium(e: MediaQueryListEvent) {
      if (e.matches) {
        setVariant(MEDIUM);
      }
    }

    function handleLarge(e: MediaQueryListEvent) {
      if (e.matches) {
        setVariant(LARGE);
      }
    }
    function handleXL(e: MediaQueryListEvent) {
      if (e.matches) {
        setVariant(LARGE);
      }
    }

    // Logical AND(&&) resolves `Property 'addEventListener' does not exist on type 'false | MediaQueryList'.` Typescript error
    smallMql && smallMql.addEventListener('change', handleSmall);
    mediumMql && mediumMql.addEventListener('change', handleMedium);
    largeMql && largeMql.addEventListener('change', handleLarge);
    xlMql && xlMql.addEventListener('change', handleXL);

    // Logical AND(&&) resolves `Property 'removeEventListener' does not exist on type 'false | MediaQueryList'.` Typescript error
    return () => {
      smallMql && smallMql.removeEventListener('change', handleSmall);
      mediumMql && mediumMql.removeEventListener('change', handleMedium);
      largeMql && largeMql.removeEventListener('change', handleLarge);
      xlMql && xlMql.removeEventListener('change', handleXL);
    };
  }, []);

  return {
    isSmall: variant === SMALL,
    isMedium: variant === MEDIUM,
    isLarge: variant === LARGE,
    isXL: variant === XL,
  };
}
